import React from "react";
import { servicesData, valueProposition } from "../data/mock";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import { Mic, Radio, Clapperboard, Music, Waves, AudioLines, Heart, Palette, Award, Layers, Zap } from "lucide-react";

const serviceIconMap = {
  Mic: Mic,
  Radio: Radio,
  Clapperboard: Clapperboard,
  Music: Music,
  Waves: Waves,
  AudioLines: AudioLines,
};

const valueIconMap = {
  Heart: Heart,
  Palette: Palette,
  Award: Award,
  Layers: Layers,
};

const Services = () => {
  return (
    <section id="services" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 backdrop-blur-md rounded-2xl py-10">
        {/* Value Proposition */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Zap className="w-5 h-5 text-teal-400" />
            <span className="font-nunito text-teal-400 text-sm tracking-[0.25em] uppercase font-semibold">
              Propuesta de valor
            </span>
          </div>

          <p className="font-nunito text-xl text-slate-200 mb-2">{valueProposition.intro}</p>
          <h2 className="font-bebas text-5xl md:text-7xl text-white tracking-wide mb-10">
            <span className="text-teal-400">{valueProposition.highlight}</span>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {valueProposition.values.map((value, idx) => {
              const IconComponent = valueIconMap[value.icon] || Heart;
              return (
                <div
                  key={idx}
                  className="group p-5 rounded-xl border border-slate-300/30 hover:border-teal-500/30 hover:bg-white/10 transition-all duration-300"
                >
                  <IconComponent className="w-6 h-6 text-teal-400 mb-3 mx-auto group-hover:scale-110 transition-transform duration-300" />
                  <p className="font-nunito text-sm text-slate-200 font-medium">{value.title}</p>
                </div>
              );
            })}
          </div>
        </div>

        <Separator className="bg-teal-500/10 mb-20" />

        {/* Services section header */}
        <div className="flex items-center gap-3 mb-6">
          <Mic className="w-5 h-5 text-teal-400" />
          <span className="font-nunito text-teal-400 text-sm tracking-[0.25em] uppercase font-semibold">Servicios</span>
          <div className="flex-1 h-px bg-teal-500/20" />
        </div>

        <h2 className="font-bebas text-5xl md:text-6xl text-black lg:text-teal-400 tracking-wide mb-12">
          Lo que puedo <span className="text-teal-400">hacer por ti</span>
        </h2>

        {/* Services grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, idx) => {
            const IconComponent = serviceIconMap[service.icon] || Mic;
            return (
              <Card
                key={idx}
                className="group backdrop-blur-md border-slate-300/30 hover:border-teal-500/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-500/5 cursor-default"
              >
                <CardContent className="p-7">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500/20 to-cyan-500/10 flex items-center justify-center mb-5 group-hover:from-teal-500/30 group-hover:to-cyan-500/20 transition-all duration-300">
                    <IconComponent className="w-7 h-7 text-teal-400 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h3 className="font-bebas text-xl text-black md:text-teal-400 tracking-wide mb-2">{service.title}</h3>
                  <p className="font-nunito text-sm text-slate-200 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
